import { useNavigate, useParams } from "react-router-dom";

import { Button, Divider, Empty, Modal, Radio, Select, Spin, Tag, message } from "antd";
import { useEffect, useMemo, useState } from "react";

import ERenewGenerateQR from "@/components/ERenewGenerateQR";
import MPGSConfirmModal from "@/components/PriceSummaryModal/MPGSConfirmModal";
import PledgesDetailsBreakdown from "@/components/PriceSummaryModal/PledgesDetailsBreakDown";
import styles from "@/components/PriceSummaryModal/PriceSummaryModal.module.css";
import VoucherModal from "@/components/PriceSummaryModal/VoucherModal";
import { IMAGES, ROUTES_URL } from "@/constant";
import {
  createMYPledge,
  createPledgesIdFromConsulate,
  createQRPayment,
  getApplicableVouchers,
  getPaymentAmount,
  getPromotionsByIds,
  initiatePaynetApi,
  reportPaynetSelectedBank,
} from "@/services/Pledge/PledgeApis";
import { useAuthStore } from "@/stores/authStore";
import { useMasterStore } from "@/stores/useMasterStore";
import { usePledgesStore } from "@/stores/usePledgesStore";
import {
  PaymentAmountDetail,
  PaymentAmountResponse,
  PromotionHistoryItem,
} from "@/types/pledgeService";
import { generateRandomDigitRef } from "@/utils";
import { getGpsCoordinates } from "@/utils/gpsCoordinates";
import { usePaymentMethodList } from "@/utils/usePaymentMethod";

interface PriceSummaryModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (url: string) => void;
}

const PriceSummaryModal: React.FC<PriceSummaryModalProps> = ({ open, onClose, onSubmit }) => {
  const navigate = useNavigate();
  const { currency } = useAuthStore();
  const { tenantMasterData, tenantMasterDataLoading, fetchTenantMasterData } = useMasterStore();
  const { id } = useParams();
  // const { selectedPledgeData, downloanAmount, cddInfo, cddType } = useRenewStore();
  const [selectedPromotion, setSelectedPromotion] = useState<PromotionHistoryItem[]>([]);
  // const [tenantMasterData, setTenantMasterData] = useState<TenantMasterDataInterface[]>([]);
  const [appliedVoucherOpenModal, setAppliedVoucherOpenModal] = useState(false);
  const [applyVoucherList, setapplyVoucherList] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState<any>(null);
  const [openGenerateQRModal, setOpenGenerateQRModal] = useState(false);
  const [qrGeneratePayload, setQrGeneratePayload] = useState<any>(null);
  const { isMalaysia, isSingapore } = useAuthStore();
  const [paynetBanks, setPaynetBanks] = useState<any[]>([]);
  const [paynetEndToEndId, setPaynetEndToEndId] = useState<string>("");
  const [paynetSignature, setPaynetSignature] = useState<string>("");
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [selectedRedirectUrl, setSelectedRedirectUrl] = useState<string | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentAmountResponse | null>(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(false);
  const { pledges } = usePledgesStore();
  const { paymentMethodDetails } = usePaymentMethodList(Number(pledges[0]?.branch?.id));
  const [isConfirmationModalForMPGS, setIsConfirmationModalForMPGS] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingForVoucher, setIsLoadingForVoucher] = useState(false);
  useEffect(() => {
    void getPaymentDetails();
  }, [selectedVoucher]);

  const handleCreateQR = (): void => {
    const payload = {
      pledges: pledges?.map((item) => ({
        old_pledge_id: item?.id,
        ...(item?.downloanAmount && { downloan_amount: item?.downloanAmount }),
      })),
      ...(selectedVoucher?.voucher_code && { voucher_code: selectedVoucher?.voucher_code }),
      payment_description: "Pledge Renew",
      bill_reference_number: generateRandomDigitRef(),
      is_downloan: getDownloanAmount(paymentDetails?.details ?? []) ? true : false,
    };
    setQrGeneratePayload(payload);
    setOpenGenerateQRModal(true);
  };

  const getApplicableVouchersHelper = async (): Promise<void> => {
    try {
      const params = {
        pledge_ids: String(pledges?.map((item) => item?.id)),
        customer_id: String(pledges?.[0]?.customer?.id),
      };
      const response = await getApplicableVouchers(params);
      setapplyVoucherList(response.data);
    } catch (error) {
      console.error("Error fetching applicable vouchers:", error);
    }
  };

  const getPaymentDetails = async (): Promise<void> => {
    setIsLoadingForVoucher(true);
    try {
      const params = {
        pledge_ids: pledges?.map((item) => item?.id).join(","),
        downloan_amount: pledges?.map((item) => item?.downloanAmount).join(","),
        voucher_code: selectedVoucher ? selectedVoucher?.voucher_code : undefined,
      };
      const response = (await getPaymentAmount(params)) as PaymentAmountResponse;
      setPaymentDetails(response);
    } catch (error) {
      console.error("Error fetch payment details", error);
    } finally {
      setIsLoadingForVoucher(false);
    }
  };

  const fetchPromotionsByIds = async (): Promise<void> => {
    try {
      const payload = {
        pledge_ids: pledges?.map((item) => item?.id),
      };
      const response = await getPromotionsByIds(payload);
      setSelectedPromotion(response?.data);
    } catch (error) {
      console.error("Error fetching customer:", error);
    }
  };

  useEffect(() => {
    fetchTenantMasterData(["P051", "P004"]);
    void getApplicableVouchersHelper();
    void fetchPromotionsByIds();
  }, [pledges]);

  const fetchPaynetBanks = async (): Promise<void> => {
    setBankLoading(true);
    const isDownloan = pledges?.reduce((acc, item) => {
      acc = acc + item?.downloanAmount;
      return acc;
    }, 0);
    let gpsCoordinates: string;

    try {
      gpsCoordinates = await getGpsCoordinates();
    } catch (error) {
      gpsCoordinates = "-1,-1";
    }
    try {
      const payload = {
        pledges: pledges?.map((item) => ({
          old_pledge_id: item?.id,
          ...(item?.downloanAmount && { downloan_amount: item?.downloanAmount }),
        })),
        ...(selectedVoucher?.voucher_code && { voucher_code: selectedVoucher?.voucher_code }),
        payment_description: "Pledge Renew",
        bill_reference_number: generateRandomDigitRef(),
        is_downloan: isDownloan ? true : false,
        x_gps_Coordinates: gpsCoordinates,
      };

      const response = await initiatePaynetApi(payload);

      setPaynetBanks(response?.banks || []);
      setPaynetEndToEndId(response?.endToEndId);
      setPaynetSignature(response?.endToEndIdSignature);
      if (response?.banks?.length === 0) {
        setBankError(true);
      } else {
        setBankError(false);
      }
    } catch (error) {
      console.error("Error fetching banks", error);
      setBankError(true);
    } finally {
      setBankLoading(false);
    }
  };

  const triggerQRPayment = async (pledgeRenewalIds: number[]): Promise<void> => {
    const resp = await Promise.all(
      pledgeRenewalIds.map((pledgeRenewalPaymentId) =>
        createQRPayment({
          pledge_renewal_payment_id: pledgeRenewalPaymentId,
        }),
      ),
    );
    const successResp = resp.find((item) => !item?.error);
    if (successResp) {
      message.success(successResp?.message);
    }
    navigate(ROUTES_URL.PLEDGE);
  };

  const createNewRequest = async (id: number): Promise<void> => {
    setLoading(true);
    try {
      const response = await createPledgesIdFromConsulate(id.toString());
      await triggerQRPayment(response?.pledge_renewal_ids ?? []);
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoading(false);
    }
  };

  const sortedRedirectUrls = useMemo(() => {
    const bank = paynetBanks?.find((item) => item?.code === selectedBank);
    const urls: any[] = bank?.redirectUrls || [];
    const seenTypes = new Set<string>();
    const uniqueList: any[] = [];

    urls.forEach((item: any) => {
      const typeKey = item?.type || item?.url;
      if (typeKey && !seenTypes.has(typeKey)) {
        seenTypes.add(typeKey);
        uniqueList.push(item);
      }
    });

    return uniqueList.sort((a: any, b: any) => {
      if (a?.type === "RET") return -1;
      if (b?.type === "RET") return 1;
      return 0;
    });
  }, [paynetBanks, selectedBank]);

  const bankOptions = useMemo(
    () =>
      paynetBanks
        ?.filter((bank) => bank.active)
        ?.map((bank) => ({
          label: bank.name,
          value: bank.code,
        })),
    [paynetBanks],
  );

  useEffect(() => {
    if (!open || !isMalaysia || !pledges?.length) return;

    setSelectedBank(null);
    setSelectedRedirectUrl(null);

    void fetchPaynetBanks();
  }, [open, pledges]);

  useEffect(() => {
    if (!selectedBank) {
      setSelectedRedirectUrl(null);
      return;
    }

    const defaultUrl =
      sortedRedirectUrls.find((item: any) => item?.type === "RET")?.url ||
      sortedRedirectUrls[0]?.url ||
      null;

    setSelectedRedirectUrl(defaultUrl);
  }, [selectedBank, sortedRedirectUrls]);

  const redirectToBank = async () => {
    if (!selectedBank) {
      message.error("Please select a bank");
      return;
    }

    if (sortedRedirectUrls.length > 1 && !selectedRedirectUrl) {
      message.error("Please select an account type");
      return;
    }

    const bankUrl = selectedRedirectUrl || sortedRedirectUrls[0]?.url;

    if (!bankUrl) {
      message.error("Something went wrong. Please try again.");
      return;
    }

    try {
      await reportPaynetSelectedBank({
        end_to_end_id: paynetEndToEndId,
        bank_code: selectedBank,
        bank_name: paynetBanks.find((bank) => bank.code === selectedBank)?.name,
      });
    } catch (error) {
      console.error("Error reporting selected bank", error);
    }

    const isFpxUrl = bankUrl.toLowerCase().includes("fpx");

    const baseUrl = isFpxUrl ? bankUrl : `${bankUrl.replace(/\/+$/, "")}/RPP/MY/Redirect/RTP`;

    const url = new URL(baseUrl);

    url.searchParams.set("EndtoEndId", paynetEndToEndId);
    url.searchParams.set("EndtoEndIdSignature", paynetSignature);
    url.searchParams.set("DbtrAgt", selectedBank);

    window.location.href = url.toString();
  };

  const onMPGSConfirmModal = (): void => {
    setIsConfirmationModalForMPGS(true);
  };
  const getDownloanAmount = (paymentDetails: PaymentAmountDetail[]): number => {
    return paymentDetails?.reduce((acc, item) => {
      return (acc = acc + item?.downloan_amount);
    }, 0);
  };
  const getPromotions = (promotions: PromotionHistoryItem[]): string[] => {
    return promotions.reduce((acc: string[], item) => {
      const promotionCode = item?.promotion?.promotion_code;
      if (promotionCode) {
        acc.push(promotionCode);
      }
      return acc;
    }, []);
  };

  return (
    <Modal
      title="Payment Summary"
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      maskClosable={false}
      centered
    >
      <Spin spinning={loading || isLoadingForVoucher || tenantMasterDataLoading || bankLoading}>
        <div className={styles.mainContainer}>
          <div className={styles.feesAndCharges}>
            <div className={styles.feesAndChargesHeader}>Fees & Charges</div>
            <Divider />
            <div className={styles.feesAndChargesBrakeDown}>
              {tenantMasterData?.length ? (
                tenantMasterData?.map((data) => (
                  <div className={styles.feesAndChargesBrakeDownItem} key={data?.id}>
                    <span>
                      {data?.name} {data?.reference_id !== "P004" && ` (${currency})`}
                    </span>{" "}
                    <span>{data?.value}</span>
                  </div>
                ))
              ) : (
                <Empty />
              )}
            </div>
          </div>

          <div className={styles.paymentBrakeDown}>
            <div className={styles.paymentBrakeDownHeader}>Payment Details</div>
            <Divider />

            <div className={styles.promotionSection}>
              <div className={styles.promotionSectionHeader}>
                <div className={styles.promotionSectionTitle}>Applied Promotion</div>
              </div>

              <div className={styles.promotionSectionBody}>
                {getPromotions(selectedPromotion)?.length ? (
                  getPromotions(selectedPromotion)?.map((item) => (
                    <Tag color="processing"> {item} </Tag>
                  ))
                ) : (
                  <div className={styles.noPromotionApplied}>No Promotion Applied</div>
                )}
              </div>
            </div>

            <span
              className="flex justify-end underline cursor-pointer font-inter text-[12px] font-semibold text-primary"
              onClick={() => setAppliedVoucherOpenModal(true)}
            >
              APPLY VOUCHER
            </span>
            <div className={styles.voucherSection}>
              <div className={styles.promotionSectionHeader}>
                <div className={styles.promotionSectionTitle}>Applied Voucher</div>
              </div>

              <div className={styles.promotionSectionBody}>
                {selectedVoucher?.voucher_code ? (
                  <div className="flex items-center gap-2">
                    <Tag
                      color="processing"
                      className={styles.customTag}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        setSelectedVoucher(null);
                      }}
                    >
                      {selectedVoucher?.voucher_code}
                    </Tag>
                  </div>
                ) : (
                  <div className={styles.noPromotionApplied}>No Voucher Applied</div>
                )}
              </div>
            </div>
            <PledgesDetailsBreakdown details={paymentDetails?.details ?? []} pledges={pledges} />

            <div className={styles.paymentBrakeDownItem}>
              <span>{`Total Pledge Amount (${currency})`}</span>{" "}
              <span>{paymentDetails?.summary?.total_pledge_amount}</span>
            </div>
            {selectedVoucher?.voucher_code && (
              <div className={styles.paymentBrakeDownItem}>
                <span>{`Voucher Discount Amount (${currency})`}</span>{" "}
                <span>{selectedVoucher?.voucher_value}</span>
              </div>
            )}
            <div className={styles.paymentBrakeDownItem}>
              <span>{`Total Interest (${currency})`}</span>{" "}
              <span>{paymentDetails?.summary?.total_interest_amount}</span>
            </div>
            <div className={styles.paymentBrakeDownItem}>
              <span>{`Admin Fee (${currency})`}</span>{" "}
              <span>{paymentDetails?.summary?.total_admin_fee}</span>
            </div>
            {getDownloanAmount(paymentDetails?.details ?? []) ? (
              <div className={styles.paymentBrakeDownItem}>
                <span>{`Total Downloan Amount (${currency})`}</span>{" "}
                <span>{getDownloanAmount(paymentDetails?.details ?? [])}</span>
              </div>
            ) : null}
            <div className={styles.paymentBrakeDownItemNetPayable}>
              <span>{`Net Payable (${currency})`}</span>{" "}
              <span>{paymentDetails?.summary?.total_payable_amount}</span>
            </div>
            {isMalaysia && paymentMethodDetails?.paynet_erenewal && (
              <>
                <div className={styles.paymentBrakeDownItem}>
                  <span>Select Bank</span>

                  <Select
                    className="w-[60%] h-[36px]"
                    placeholder="Select Bank"
                    value={selectedBank || undefined}
                    onChange={(value) => {
                      setSelectedBank(value);
                      setSelectedRedirectUrl(null);
                    }}
                    loading={bankLoading}
                    notFoundContent={
                      bankLoading ? (
                        <div className="flex flex-col justify-center items-center p-12">
                          <Spin size="small" />
                          <div>Searching bank list...</div>
                        </div>
                      ) : (
                        "No banks found"
                      )
                    }
                    options={bankOptions}
                  />
                </div>

                {sortedRedirectUrls.length > 1 && (
                  <div className={styles.paymentBrakeDownItem}>
                    <span>Account Type</span>

                    <Radio.Group
                      className="w-[60%] flex gap-6 items-center"
                      value={selectedRedirectUrl || undefined}
                      onChange={(e) => setSelectedRedirectUrl(e.target.value)}
                    >
                      {sortedRedirectUrls.map((item: any) => {
                        const label =
                          item?.type === "RET"
                            ? "Personal"
                            : item?.type === "COR"
                              ? "Business"
                              : item?.type || "Personal";

                        return (
                          <Radio key={item?.url} value={item?.url}>
                            {label}
                          </Radio>
                        );
                      })}
                    </Radio.Group>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.buttonWrapper}>
            {isMalaysia ? (
              paymentMethodDetails?.paynet_erenewal ? (
                <>
                  {bankError ? (
                    <Button className={styles.proceedToPayButton} onClick={fetchPaynetBanks}>
                      <img src={IMAGES.DUIT_NOW_ICON} className={styles.imageLogo} />
                      Retry Online Banking/Wallets
                    </Button>
                  ) : (
                    <Button className={styles.proceedToPayButton} onClick={redirectToBank}>
                      <img src={IMAGES.DUIT_NOW_ICON} className={styles.imageLogo} />
                      Online Banking/Wallets
                    </Button>
                  )}
                </>
              ) : (
                <div className={styles.wrapperDetails}>
                  <img
                    src={IMAGES.WARNING_ICON_BRANCH}
                    alt="Basic Details"
                    className={styles.icon}
                  />
                  <h3 className={styles.sectionTitle}>No payment methods are configured</h3>
                </div>
              )
            ) : null}
            {isSingapore ? (
              !paymentMethodDetails?.paynow_qr_retail && !paymentMethodDetails?.mpges_erenewal ? (
                <div className={styles.wrapperDetails}>
                  <img
                    src={IMAGES.WARNING_ICON_BRANCH}
                    alt="Basic Details"
                    className={styles.icon}
                  />
                  <h3 className={styles.sectionTitle}>No payment methods are configured</h3>
                </div>
              ) : (
                <>
                  {paymentMethodDetails?.paynow_qr_retail && (
                    <Button className={styles.proceedToPayButton} onClick={handleCreateQR}>
                      Create QR
                    </Button>
                  )}
                  {paymentMethodDetails?.mpges_erenewal && (
                    <Button className={styles.proceedToPayButton} onClick={onMPGSConfirmModal}>
                      Pay With MPGS
                    </Button>
                  )}
                </>
              )
            ) : null}
          </div>
          {openGenerateQRModal && (
            <ERenewGenerateQR
              open={openGenerateQRModal}
              close={() => setOpenGenerateQRModal(false)}
              qrGeneratePayload={qrGeneratePayload}
              onPaymentSuccess={(id) => {
                void createNewRequest(id);
              }}
            />
          )}
          <VoucherModal
            open={appliedVoucherOpenModal}
            onClose={() => setAppliedVoucherOpenModal(false)}
            data={applyVoucherList}
            setSelectedVoucher={setSelectedVoucher}
            selectedVoucher={selectedVoucher}
          />
          {isConfirmationModalForMPGS && (
            <MPGSConfirmModal
              open={isConfirmationModalForMPGS}
              onClose={() => setIsConfirmationModalForMPGS(false)}
              selectedVoucher={selectedVoucher}
              tenantMasterData={tenantMasterData}
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default PriceSummaryModal;
